import { FormEvent, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@itaas/core/auth/useAuth";
import { PatientFormModal } from "@/components/patients/PatientFormModal";
import { PageHeader } from "@/components/PageHeader";
import { TablePagination } from "@/components/TablePagination";
import { useClientPagination } from "@/hooks/useClientPagination";
import { ServiceQueuePanel } from "@/components/queue/ServiceQueuePanel";
import {
  admissionCustomerName,
  createAdmission,
  handleConsultationPaid,
  linkAdmissionBillingDocument,
  listAdmissionsByStatus,
  orientAdmission,
  subscribeConsultationPaid,
  syncAdmissionsFromApi,
  updateAdmissionStatus,
} from "@/core/admissions/admission-store";
import {
  canOrientToClinicalConsultation,
  filterOrientationDestinations,
  isPaidClinicalDestinationDisabled,
  requiresConsultationPaymentGate,
} from "@/core/admissions/coverage-rules";
import {
  ADMISSION_STATUS_LABELS,
  DESTINATION_OPTIONS,
  type Admission,
  type AdmissionStatus,
} from "@/core/admissions/types";
import {
  getReceptionSettings,
  listActiveMedicalUnitNames,
  subscribeSettingsChange,
} from "@/core/admin/establishment-store";
import {
  createConsultationProformaAtReception,
  getBillingDocumentByAdmission,
} from "@/core/billing/billing-documents/billing-documents-store";
import { BILLING_DOC_STATUS_LABELS } from "@/core/billing/billing-documents/types";
import {
  createPatient,
  getPatientById,
  listPatients,
  patientDetailPath,
} from "@/core/patients/patient-store";
import {
  hasThirdPartyCoverage,
  patientCoverageLabel,
  patientFullName,
} from "@/core/patients/types";
import { canWriteAdmissions, canWritePatients } from "@/core/permissions";
import {
  admitPatientForLabResults,
  countResultsReady,
  listResultsWaiting,
} from "@/core/reception/results-waiting-store";
import { RESULTS_WAITING_STATUS_LABELS } from "@/core/reception/types";
import { routes } from "@/router/all_routes";

const BOARD_STATUSES: AdmissionStatus[] = [
  "waiting",
  "in_progress",
  "pending_billing",
  "oriented",
];

function AdmissionCard({
  admission,
  onRefresh,
  canWrite,
}: {
  admission: Admission;
  onRefresh: () => void;
  canWrite: boolean;
}) {
  const patient = getPatientById(admission.patientId);
  const destinations = useMemo(() => {
    const units = listActiveMedicalUnitNames();
    const list = units.length > 0 ? units : [...DESTINATION_OPTIONS];
    return filterOrientationDestinations(list);
  }, []);
  const receptionSettings = getReceptionSettings();
  const [selectedDestinations, setSelectedDestinations] = useState<string[]>([]);
  const [cardMsg, setCardMsg] = useState<string | null>(null);

  const billingDoc = getBillingDocumentByAdmission(admission.id);
  const customerName = admissionCustomerName(admission);

  if (!patient && !admission.walkInName) return null;

  const handleStart = () => {
    updateAdmissionStatus(admission.id, "in_progress");
    onRefresh();
  };

  const toggleDestination = (dest: string) => {
    if (isPaidClinicalDestinationDisabled(dest, patient ?? null, admission.id)) return;
    setSelectedDestinations((prev) =>
      prev.includes(dest) ? prev.filter((d) => d !== dest) : [...prev, dest],
    );
  };

  const clinicalBlockedHint = (dest: string): string | null => {
    if (!patient || !requiresConsultationPaymentGate(dest)) return null;
    if (!isPaidClinicalDestinationDisabled(dest, patient, admission.id)) return null;
    const gate = canOrientToClinicalConsultation(patient, admission.id);
    return gate.reason ?? "Consultation payante — non disponible.";
  };

  const handleOrient = (e: FormEvent) => {
    e.preventDefault();
    const allowedSelection = selectedDestinations.filter(
      (d) => !isPaidClinicalDestinationDisabled(d, patient ?? null, admission.id),
    );
    if (allowedSelection.length === 0) {
      setCardMsg("Sélectionnez au moins un service autorisé (consultations payantes grisées).");
      return;
    }
    const result = orientAdmission(admission.id, allowedSelection);
    if (!result) return;

    if (result.enqueuedDestinations.length > 0) {
      setCardMsg(`Orienté vers : ${result.enqueuedDestinations.join(" · ")}`);
      setSelectedDestinations([]);
    } else {
      setCardMsg("Aucune orientation effectuée — consultations payantes non éligibles.");
    }
    onRefresh();
  };

  const handleProforma = () => {
    if (!patient) {
      setCardMsg("Enregistrez le patient ou saisissez un nom visiteur.");
      return;
    }
    const doc = createConsultationProformaAtReception(admission.id, {
      patientId: admission.patientId,
      customerName,
      serviceLabel: "Médecine générale",
    });
    if (!doc) {
      setCardMsg("Proforma accueil non autorisée — voir paramètres administrateur.");
      return;
    }
    linkAdmissionBillingDocument(admission.id, doc.id);
    setCardMsg(
      `Proforma ${doc.number} émise — envoi caisse pour règlement avant consultation.`,
    );
    onRefresh();
  };

  const canProforma =
    receptionSettings.allowReceptionConsultationProforma &&
    patient &&
    !hasThirdPartyCoverage(patient) &&
    !billingDoc;

  return (
    <article className="hosp-adm-card">
      <div className="hosp-adm-card-head">
        <div>
          <span className="ph-inv-badge">{patient?.fileNumber ?? "VISITEUR"}</span>
          <h4 className="hosp-adm-card-name mb-0 mt-2">
            {patient ? (
              <Link to={patientDetailPath(patient.id)}>{patientFullName(patient)}</Link>
            ) : (
              admission.walkInName
            )}
          </h4>
        </div>
        <span className={`hosp-status-badge hosp-status-badge--${admission.status}`}>
          {ADMISSION_STATUS_LABELS[admission.status]}
        </span>
      </div>

      {patient && (
        <p className="small mb-2">
          <span className="text-muted">Couverture :</span> {patientCoverageLabel(patient)}
        </p>
      )}

      <p className="small mb-2">
        <span className="text-muted">Motif :</span> {admission.motif || "—"}
      </p>
      <p className="small text-muted mb-3">
        Arrivée{" "}
        {new Date(admission.createdAt).toLocaleTimeString("fr-FR", {
          hour: "2-digit",
          minute: "2-digit",
        })}
      </p>

      {billingDoc && (
        <p className="small mb-2">
          <span className="text-muted">Facturation :</span>{" "}
          <span className="badge bg-secondary">
            {billingDoc.number} — {BILLING_DOC_STATUS_LABELS[billingDoc.status]}
          </span>
          {billingDoc.status !== "paid" && billingDoc.payerType !== "insurer" && (
            <Link
              to={routes.billingCashier}
              className="btn btn-success btn-sm ms-2 mt-1"
            >
              Caisse
            </Link>
          )}
        </p>
      )}

      {cardMsg && <p className="small text-info mb-2">{cardMsg}</p>}

      {admission.status === "waiting" && canWrite && (
        <button type="button" className="btn btn-primary btn-sm w-100" onClick={handleStart}>
          Prendre en charge
        </button>
      )}

      {admission.status === "in_progress" && canWrite && (
        <>
          {canProforma && (
            <button
              type="button"
              className="btn btn-outline-primary btn-sm w-100 mb-2"
              onClick={handleProforma}
            >
              Proforma consultation (généraliste)
            </button>
          )}
          <form onSubmit={handleOrient}>
            <p className="form-label small fw-semibold mb-1">
              Orienter vers (plusieurs services possibles)
            </p>
            <div className="d-flex flex-column gap-1 mb-2">
              {destinations.map((opt) => {
                const blockHint = clinicalBlockedHint(opt);
                const isDisabled = isPaidClinicalDestinationDisabled(
                  opt,
                  patient ?? null,
                  admission.id,
                );
                return (
                  <div
                    key={opt}
                    className={isDisabled ? "hosp-adm-dest--disabled" : undefined}
                  >
                    <label className="form-check form-check-sm mb-0">
                      <input
                        type="checkbox"
                        className="form-check-input"
                        checked={selectedDestinations.includes(opt)}
                        disabled={isDisabled}
                        onChange={() => toggleDestination(opt)}
                      />
                      <span className="form-check-label small">{opt}</span>
                    </label>
                    {blockHint && (
                      <p className="small text-muted mb-0 ms-4">{blockHint}</p>
                    )}
                  </div>
                );
              })}
            </div>
            <button
              type="submit"
              className="btn btn-success btn-sm w-100"
              disabled={
                selectedDestinations.filter(
                  (d) => !isPaidClinicalDestinationDisabled(d, patient ?? null, admission.id),
                ).length === 0
              }
            >
              Valider l&apos;orientation ({selectedDestinations.length})
            </button>
          </form>
        </>
      )}

      {admission.status === "pending_billing" && (
        <div className="small">
          <p className="text-warning mb-2">
            Patient non assuré — orientation clinique bloquée. Dossier transmis à la
            comptabilité.
          </p>
          {admission.blockedDestinations.length > 0 && (
            <p className="mb-2">
              <span className="text-muted">Services demandés :</span>{" "}
              {admission.blockedDestinations.join(" · ")}
            </p>
          )}
          <Link to={routes.billingAccounting} className="btn btn-outline-secondary btn-sm">
            Ouvrir comptabilité
          </Link>
        </div>
      )}

      {admission.status === "oriented" && (
        <p className="small mb-0">
          <span className="text-muted">Destination(s) :</span>{" "}
          <strong>
            {admission.destinations?.length
              ? admission.destinations.join(" · ")
              : admission.destination || "—"}
          </strong>
          {admission.blockedDestinations.length > 0 && (
            <span className="d-block text-warning mt-1">
              Bloqué (comptabilité) : {admission.blockedDestinations.join(" · ")}
            </span>
          )}
        </p>
      )}
    </article>
  );
}

function StatusColumn({
  status,
  admissions,
  onRefresh,
  canWrite,
}: {
  status: AdmissionStatus;
  admissions: Admission[];
  onRefresh: () => void;
  canWrite: boolean;
}) {
  return (
    <div className="hosp-adm-column">
      <div className="hosp-adm-column-head">
        <h3 className="hosp-adm-column-title">{ADMISSION_STATUS_LABELS[status]}</h3>
        <span className="hosp-adm-count">{admissions.length}</span>
      </div>
      <div className="hosp-adm-column-body">
        {admissions.length === 0 ? (
          <p className="small text-muted mb-0">Aucun dossier</p>
        ) : (
          admissions.map((admission) => (
            <AdmissionCard
              key={admission.id}
              admission={admission}
              onRefresh={onRefresh}
              canWrite={canWrite}
            />
          ))
        )}
      </div>
    </div>
  );
}

export function AdmissionsPage() {
  const auth = useAuth();
  const canWrite = canWriteAdmissions(auth);
  const canCreatePatient = canWritePatients(auth);
  const [refreshKey, setRefreshKey] = useState(0);
  const [syncing, setSyncing] = useState(false);
  const [patientModalOpen, setPatientModalOpen] = useState(false);
  const [patientId, setPatientId] = useState("");
  const [walkInName, setWalkInName] = useState("");
  const [motif, setMotif] = useState("");
  const [formError, setFormError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setSyncing(true);
    void syncAdmissionsFromApi().finally(() => {
      if (!cancelled) {
        setSyncing(false);
        setRefreshKey((k) => k + 1);
      }
    });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const refresh = () => setRefreshKey((k) => k + 1);
    const unsubPaid = subscribeConsultationPaid((admissionId, docId) => {
      handleConsultationPaid(admissionId, docId);
      refresh();
    });
    window.addEventListener("hospitol:admissions", refresh);
    window.addEventListener("hospitol:billing-documents", refresh);
    const unsubSettings = subscribeSettingsChange(refresh);
    return () => {
      unsubPaid();
      unsubSettings();
      window.removeEventListener("hospitol:admissions", refresh);
      window.removeEventListener("hospitol:billing-documents", refresh);
    };
  }, []);

  const byStatus = useMemo(() => {
    void refreshKey;
    return BOARD_STATUSES.reduce(
      (acc, status) => {
        acc[status] = listAdmissionsByStatus(status, "admission");
        return acc;
      },
      {} as Record<AdmissionStatus, Admission[]>,
    );
  }, [refreshKey]);

  const patients = useMemo(() => listPatients(), [refreshKey, patientModalOpen]);

  const refresh = () => setRefreshKey((k) => k + 1);

  const handleRegister = (e: FormEvent) => {
    e.preventDefault();
    let resolvedPatientId = patientId;

    if (!resolvedPatientId && walkInName.trim()) {
      const created = createPatient({
        firstName: walkInName.trim(),
        lastName: "(visiteur)",
        gender: "other",
        patientType: "outpatient",
        coverageType: "none",
        phone: "",
        address: "",
      });
      resolvedPatientId = created.id;
    }

    if (!resolvedPatientId) {
      setFormError("Sélectionnez un patient ou saisissez le nom du visiteur.");
      return;
    }
    if (!motif.trim()) {
      setFormError("Indiquez un motif de venue.");
      return;
    }
    createAdmission({
      patientId: resolvedPatientId,
      channel: "admission",
      motif,
      walkInName: walkInName.trim() || undefined,
    });
    setMotif("");
    setWalkInName("");
    setFormError(null);
    refresh();
  };

  return (
    <div>
      <PageHeader
        title="Admissions"
        description="Accueil, proforma consultation, file des patients payés et orientation."
        actions={
          <div className="ph-page-header-actions">
            <Link to={routes.patients} className="btn btn-outline-secondary btn-sm">
              Registre patients
            </Link>
          </div>
        }
      />

      <ServiceQueuePanel
        serviceId="admission"
        defaultUnitName="Accueil"
        title="File d'attente — accueil (nouvelles admissions)"
        ticketFilter={(t) => !t.billingDocumentId && t.unitName === "Accueil"}
      />

      <ServiceQueuePanel
        serviceId="admission"
        defaultUnitName="Médecine générale"
        title="File d'attente — consultations payées (généraliste)"
        ticketFilter={(t) => Boolean(t.billingDocumentId)}
      />

      <ResultsWaitingPanel canWrite={canWrite} onRefresh={refresh} refreshKey={refreshKey} />

      {canWrite && (
      <div className="ph-invoice-sheet ph-adm-register mb-4">
        <h3 className="h6 fw-semibold mb-3">Nouvelle admission</h3>
        <form className="row g-3 align-items-end ph-adm-register-form" onSubmit={handleRegister}>
          <div className="col-12 col-md-4">
            <label className="form-label small fw-semibold mb-1" htmlFor="adm-patient">
              Patient enregistré
            </label>
            <div className="d-flex gap-2 ph-adm-patient-row">
              <select
                id="adm-patient"
                className="form-select form-select-sm"
                value={patientId}
                onChange={(e) => setPatientId(e.target.value)}
              >
                <option value="">— Choisir —</option>
                {patients.map((p) => (
                  <option key={p.id} value={p.id}>
                    {patientFullName(p)} ({p.fileNumber})
                  </option>
                ))}
              </select>
              <button
                type="button"
                className="btn btn-outline-primary btn-sm text-nowrap"
                onClick={() => setPatientModalOpen(true)}
                disabled={!canCreatePatient}
              >
                + Créer
              </button>
            </div>
          </div>
          <div className="col-12 col-md-3">
            <label className="form-label small fw-semibold mb-1" htmlFor="adm-walkin">
              Ou nom visiteur (non assuré)
            </label>
            <input
              id="adm-walkin"
              type="text"
              className="form-control form-control-sm"
              placeholder="Si pas de dossier patient"
              value={walkInName}
              disabled={Boolean(patientId)}
              onChange={(e) => setWalkInName(e.target.value)}
            />
          </div>
          <div className="col-12 col-md-3">
            <label className="form-label small fw-semibold mb-1" htmlFor="adm-motif">
              Motif
            </label>
            <input
              id="adm-motif"
              type="text"
              className="form-control form-control-sm"
              placeholder="Ex. consultation généraliste…"
              value={motif}
              onChange={(e) => setMotif(e.target.value)}
            />
          </div>
          <div className="col-12 col-md-2">
            <button type="submit" className="btn btn-primary btn-sm w-100">
              Enregistrer
            </button>
          </div>
        </form>
        {formError && <p className="small text-danger mb-0 mt-2">{formError}</p>}
        {!getReceptionSettings().allowReceptionConsultationProforma && (
          <p className="small text-muted mb-0 mt-2">
            Proforma consultation à l&apos;accueil désactivée — les consultations payantes
            restent grisées tant que le patient n&apos;est pas assuré ou n&apos;a pas réglé à la
            caisse.
          </p>
        )}
      </div>
      )}

      {syncing && (
        <p className="small text-muted mb-2">Synchronisation des admissions…</p>
      )}

      <div className="hosp-adm-board-hint d-md-none small text-muted mb-2">
        Glissez horizontalement pour parcourir les colonnes du flux admissions.
      </div>
      <div className="hosp-adm-board">
        {BOARD_STATUSES.map((status) => (
          <StatusColumn
            key={status}
            status={status}
            admissions={byStatus[status]}
            onRefresh={refresh}
            canWrite={canWrite}
          />
        ))}
      </div>

      <PatientFormModal
        open={patientModalOpen && canCreatePatient}
        onClose={() => setPatientModalOpen(false)}
        onSave={(input) => {
          const created = createPatient(input);
          setPatientId(created.id);
          setWalkInName("");
          setPatientModalOpen(false);
          refresh();
        }}
      />
    </div>
  );
}

function ResultsWaitingPanel({
  canWrite,
  onRefresh,
  refreshKey,
}: {
  canWrite: boolean;
  onRefresh: () => void;
  refreshKey: number;
}) {
  const [msg, setMsg] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<"ready" | "awaiting_analysis">("ready");

  const entries = useMemo(() => {
    void refreshKey;
    return listResultsWaiting({ status: statusFilter });
  }, [refreshKey, statusFilter]);

  const pagination = useClientPagination(entries, [statusFilter], 25);
  const { pagedItems } = pagination;

  const readyCount = useMemo(() => {
    void refreshKey;
    return countResultsReady();
  }, [refreshKey]);

  useEffect(() => {
    const refresh = () => onRefresh();
    window.addEventListener("hospitol:results-waiting", refresh);
    return () => window.removeEventListener("hospitol:results-waiting", refresh);
  }, [onRefresh]);

  const handleAdmit = (entryId: string) => {
    const result = admitPatientForLabResults(entryId);
    if (!result) {
      setMsg("Accueil impossible — résultat non disponible.");
      return;
    }
    setMsg("Patient accueilli et orienté vers consultation résultats.");
    onRefresh();
  };

  return (
    <div className="ph-invoice-sheet ph-adm-results mb-4">
      <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3 ph-split-detail-head">
        <div>
          <h3 className="h6 fw-semibold mb-1">Retour résultats laboratoire</h3>
          <p className="small text-muted mb-0">
            L&apos;accueil voit qui attend un résultat (sans détail clinique) et oriente vers le
            médecin pour lecture des résultats.
          </p>
        </div>
        {readyCount > 0 && (
          <span className="badge text-bg-success">{readyCount} disponible(s)</span>
        )}
      </div>

      {msg && <div className="alert alert-success py-2 small">{msg}</div>}

      <div className="d-flex flex-wrap gap-2 mb-3 ph-adm-results-filters">
        <button
          type="button"
          className={`btn btn-sm ${statusFilter === "ready" ? "btn-primary" : "btn-outline-secondary"}`}
          onClick={() => setStatusFilter("ready")}
        >
          Résultats disponibles
        </button>
        <button
          type="button"
          className={`btn btn-sm ${statusFilter === "awaiting_analysis" ? "btn-primary" : "btn-outline-secondary"}`}
          onClick={() => setStatusFilter("awaiting_analysis")}
        >
          Analyses en cours
        </button>
      </div>

      {entries.length === 0 ? (
        <p className="small text-muted mb-0">Aucun patient dans cette file.</p>
      ) : (
        <div className="table-responsive ph-adm-results-table ph-diag-table">
          <table className="table table-sm ph-dash-table mb-0">
            <thead>
              <tr>
                <th>Patient</th>
                <th>Analyse</th>
                <th>N° demande</th>
                <th>Statut</th>
                <th>Disponible le</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {pagedItems.map((entry) => {
                const patient = getPatientById(entry.patientId);
                return (
                  <tr key={entry.id} className="ph-diag-row">
                    <td data-label="Patient">
                      {patient ? (
                        <Link to={patientDetailPath(patient.id)}>{patientFullName(patient)}</Link>
                      ) : (
                        "—"
                      )}
                    </td>
                    <td className="small" data-label="Analyse">
                      {entry.testName}
                    </td>
                    <td data-label="N° demande">
                      <span className="ph-inv-badge">{entry.orderNumber}</span>
                    </td>
                    <td className="small" data-label="Statut">
                      {RESULTS_WAITING_STATUS_LABELS[entry.status]}
                    </td>
                    <td className="small text-muted" data-label="Disponible le">
                      {entry.resultReadyAt
                        ? new Date(entry.resultReadyAt).toLocaleString("fr-FR")
                        : "—"}
                    </td>
                    <td className="text-end ph-diag-no-label" data-label="">
                      {canWrite && entry.status === "ready" && (
                        <button
                          type="button"
                          className="btn btn-primary btn-sm"
                          onClick={() => handleAdmit(entry.id)}
                        >
                          Accueillir → consultation résultats
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          <TablePagination
            page={pagination.page}
            pageSize={pagination.pageSize}
            totalItems={pagination.totalItems}
            onPageChange={pagination.setPage}
            onPageSizeChange={pagination.setPageSize}
          />
        </div>
      )}
    </div>
  );
}
